// ============================================================================
// photo-resolution-policy.js — DEC-COUNCIL-007 / Candidate C6 Slice 2
// ----------------------------------------------------------------------------
// Internal seam shared by the two photo-resolution cascades:
//
//   - src/utils/use-photo-src.js          (RENDER-side: async-aware,
//                                          accepts any non-blob URL,
//                                          knows blobId + s3Key fetch paths)
//   - src/services/photo-client-images.js (SYNC INSPECTION: markup-aware,
//                                          strict data:image/* base64 only,
//                                          consumed by PDF gen + persistence)
//
// These two cascades have legitimately different purposes — they should NOT
// merge into one resolver. But the Council flagged that they treat the
// candidate fields (data / dataUrl / originalData / aiMarkupData) inconsistently
// today, with subtle drift that has bitten before (a photo with only
// `originalData` set rendered from neither). This module concentrates the
// SHARED primitives so the two cascades stay in lockstep on:
//
//   - WHICH fields may carry inline bytes (INLINE_CANDIDATE_FIELDS)
//   - HOW to classify markup state (isManualPhotoMarkup / isAiOnlyPhotoMarkup)
//   - HOW to validate a candidate URL for each cascade's purpose
//     (isUsableDisplayUrl vs isStrictDataUrl)
//
// What we explicitly do NOT consolidate:
//   - The per-cascade ORDER of fields (each cascade may prefer different
//     fields based on its audience: render vs client-clean vs markup-review)
//   - The async fetch paths (blob/S3) — those stay in use-photo-src.js
//   - The markup-rewriting (`getClientFacingPhoto`) — that stays in
//     photo-client-images.js
//
// Test surface lives at src/__tests__/photo-resolution-policy.unit.test.js.
// ============================================================================

/**
 * Predicate: is this an object photo (vs a string URL or null)?
 */
export function isObjectPhoto(photo: unknown): photo is Record<string, any> {
    return !!photo && typeof photo === 'object' && !Array.isArray(photo);
}

/**
 * Predicate: is this a strict data:image/* base64 URL?
 *
 * Used by photo-client-images.js for sync inspection (PDF generation,
 * persistence stripping). Stricter than `isUsableDisplayUrl` — only
 * accepts genuine inline image bytes, NOT http URLs / signed URLs.
 */
export function isStrictDataUrl(value: unknown): boolean {
    return typeof value === 'string'
        && /^data:image\/(?:png|jpe?g|webp|gif);base64,/i.test(value);
}

/**
 * Predicate: is this a URL usable for `<img src={}>` rendering?
 *
 * Used by use-photo-src.js for the render-side cascade. Accepts:
 *   - data: URLs (any image format)
 *   - http(s):// URLs (signed S3 URLs, CDN URLs)
 * Rejects:
 *   - blob: URLs (these die after browser reload and should never mask a
 *     valid durable backing reference like blobId/s3Key on restored projects)
 *   - empty / non-string values
 */
export function isUsableDisplayUrl(value: unknown): boolean {
    return typeof value === 'string'
        && value.length > 0
        && !value.startsWith('blob:');
}

/**
 * Predicate: does this photo carry a manual (operator-made) markup?
 *
 * Manual markup is durable — the operator chose what shows to the client.
 * Both cascades treat manual-markup photos as "show the data as-is."
 */
export function isManualPhotoMarkup(photo: unknown): boolean {
    if (!isObjectPhoto(photo)) return false;
    const source = typeof photo.markupSource === 'string' ? photo.markupSource.toLowerCase() : '';
    return !!photo.manualEditedAt || source === 'manual';
}

/**
 * Predicate: does this photo carry an AI-only markup (no manual edit)?
 *
 * QuoteMaker stopped drawing AI markup on 2026-10-13, but photos saved
 * before then still carry it. It must NOT leak to client PDFs or hero
 * images: client-facing surfaces strip it (returning the pristine
 * originalData if available), and no surface shows it any more.
 */
export function isAiOnlyPhotoMarkup(photo: unknown): boolean {
    if (!isObjectPhoto(photo) || isManualPhotoMarkup(photo)) return false;
    const source = typeof photo.markupSource === 'string' ? photo.markupSource.toLowerCase() : '';
    const hasAiEvidence = !!photo.aiAnnotated
        || source === 'ai'
        || source === 'auto'
        || source === 'pipeline'
        || isStrictDataUrl(photo.aiMarkupData)
        || (Array.isArray(photo.aiMarkupRegions) && photo.aiMarkupRegions.length > 0);
    return hasAiEvidence;
}

/**
 * Predicate: should this photo be called "marked up"?
 *
 * Only markup a person drew counts. The removed AI pass stamped
 * `markedUp: true` on every photo it annotated, so that flag alone is not
 * enough: a photo the AI marked up is not "marked up". Manual saves made
 * before 2026-04-25 stamped only `markedUp` (no manualEditedAt and no
 * markupSource), so a markedUp photo with no AI evidence still counts.
 */
export function isManuallyMarkedUp(photo: unknown): boolean {
    if (!isObjectPhoto(photo)) return false;
    if (isManualPhotoMarkup(photo)) return true;
    return !!photo.markedUp && !isAiOnlyPhotoMarkup(photo);
}

/**
 * Ordered list of fields that may carry inline bytes (data URL OR usable
 * display URL, depending on cascade).
 *
 * Both cascades agree on this canonical list. Per-cascade ordering may
 * differ (e.g., the client-facing cascade prefers originalData over data
 * for AI-only photos because data is the AI-marked version), but the SET
 * of candidate fields is identical.
 */
export const INLINE_CANDIDATE_FIELDS = Object.freeze([
    'data',
    'dataUrl',
    'thumbnailUrl',
    'originalData',
    'clientData',
    'cleanData',
    'aiMarkupData',
]);

/**
 * Pick the first field from `fields` whose value passes the `predicate`.
 *
 * Generic helper used by both cascades. The caller supplies the field
 * ORDER + the predicate, so each cascade keeps its own ordering policy.
 *
 * @param {object} photo
 * @param {string[]} fields - in priority order
 * @param {(value: any) => boolean} predicate
 * @returns {string|null} the first matching value, or null
 */
export function pickFirstField(photo: unknown, fields: string[], predicate: (value: any) => boolean) {
    if (!isObjectPhoto(photo)) return null;
    for (const field of fields) {
        const value = photo[field];
        if (predicate(value)) return value;
    }
    return null;
}

/**
 * Convenience: pick the first field whose value is a strict data:image/* URL.
 * Used by photo-client-images.js for the sync inspection cascade.
 */
export function pickFirstStrictDataUrl(photo: unknown, fields: string[]) {
    return pickFirstField(photo, fields, isStrictDataUrl);
}

/**
 * Convenience: pick the first field whose value is a usable display URL.
 * Used by use-photo-src.js for the render-side cascade.
 */
export function pickFirstUsableDisplayUrl(photo: unknown, fields: string[]) {
    return pickFirstField(photo, fields, isUsableDisplayUrl);
}
